import { useState } from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { MessageAction } from '@/components/ai-elements/message';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

interface Props {
  question: string;
  answer: string;
  threadId: string | null;
  route: string;
}

/**
 * Helpful / Not helpful rating on an Ask Cannafax answer. Stores a snapshot of the
 * question + answer so a reported miss survives thread deletion and can be promoted
 * into the golden eval set (src/test/data/askCannafaxEvalCases.ts).
 */
export function AnswerFeedback({ question, answer, threadId, route }: Props) {
  const [rated, setRated] = useState<1 | -1 | null>(null);
  const [askComment, setAskComment] = useState(false);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (rating: 1 | -1, note?: string) => {
    setSaving(true);
    const { error } = await supabase.from('ask_cannafax_answer_feedback' as any).insert({
      rating,
      question,
      answer,
      route,
      thread_id: threadId,
      comment: note?.trim() || null,
    });
    setSaving(false);
    if (error) {
      toast.error("Couldn't save your rating", { description: 'Please try again.' });
      return false;
    }
    setRated(rating);
    return true;
  };

  if (rated === 1) return <span className="text-xs text-muted-foreground">Thanks!</span>;
  if (rated === -1 && !askComment)
    return <span className="text-xs text-muted-foreground">Thanks — we'll use this to fix it.</span>;

  if (askComment) {
    return (
      <div className="w-full space-y-2 pt-1">
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="What was wrong or missing? (optional)"
          className="min-h-[60px] text-sm"
          maxLength={2000}
        />
        <div className="flex gap-2">
          <Button
            size="sm"
            disabled={saving}
            onClick={async () => {
              if (await submit(-1, comment)) setAskComment(false);
            }}
          >
            Send
          </Button>
          <Button size="sm" variant="quiet" onClick={() => setAskComment(false)}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <MessageAction tooltip="Helpful" disabled={saving} onClick={() => void submit(1)}>
        <ThumbsUp className="h-3.5 w-3.5" />
      </MessageAction>
      <MessageAction tooltip="Not helpful" disabled={saving} onClick={() => setAskComment(true)}>
        <ThumbsDown className="h-3.5 w-3.5" />
      </MessageAction>
    </>
  );
}
